import type { QgammonAbandonedReason, QgammonEndReason, QgammonMatchSummary } from '@/types/qgammon';

/**
 * How a finished QGammon match ended, resolved for display.
 *
 * Two independent server columns answer this — `endReason` on `completed`
 * matches, `abandonedReason` on `abandoned` ones — and BOTH are additive and
 * nullable: they arrive as `undefined` from a qgammon-api build predating them
 * and as `null` on rows written before the `20260818000000_add_end_reason`
 * migration. So the resolution is deliberately three-tiered:
 *
 *   1. the column, when present AND a value we know how to label;
 *   2. otherwise the STATUS heuristic — `abandoned` alone still tells the user
 *      the match was interrupted, which is the part they care about;
 *   3. otherwise nothing. A `completed` match with no reason renders no badge,
 *      exactly as it does today: the win/loss marker already carries the
 *      outcome, and inventing "played out" for a row we know nothing about
 *      would be a claim, not a fallback.
 *
 * Unknown FUTURE values fall to tier 2/3 rather than being shown raw — both
 * columns are strings on purpose server-side, so new reasons WILL appear and
 * must degrade instead of leaking an identifier into the UI.
 *
 * Tier 2 is NOT dead weight now that the migration has landed in production
 * (2026-08-30 ~08:53Z). Rows written BEFORE it keep `endReason` NULL forever —
 * the column was backfilled with nothing, because the information never
 * existed. So from today this resolver is the only thing holding the old
 * history and the new one in one list: without tier 2 every pre-migration
 * match would silently lose the one fact the user cares about.
 */
export type QgammonEndDescriptor =
  | { source: 'endReason'; key: QgammonEndReason }
  | { source: 'abandonedReason'; key: QgammonAbandonedReason }
  | { source: 'status'; key: 'abandoned' }
  | null;

const KNOWN_END_REASONS: readonly QgammonEndReason[] = [
  'normal',
  'resign',
  'timeout',
  'graceForfeit',
];

const KNOWN_ABANDONED_REASONS: readonly QgammonAbandonedReason[] = [
  'server_restart',
  'never_started',
];

function isEndReason(v: string): v is QgammonEndReason {
  return (KNOWN_END_REASONS as readonly string[]).includes(v);
}

function isAbandonedReason(v: string): v is QgammonAbandonedReason {
  return (KNOWN_ABANDONED_REASONS as readonly string[]).includes(v);
}

/** Resolve why a match ended. Returns null when nothing truthful can be said. */
export function matchEndDescriptor(m: QgammonMatchSummary): QgammonEndDescriptor {
  if (m.status === 'abandoned') {
    const reason = m.abandonedReason;
    if (reason && isAbandonedReason(reason)) return { source: 'abandonedReason', key: reason };
    return { source: 'status', key: 'abandoned' };
  }
  if (m.status === 'completed') {
    const reason = m.endReason;
    if (reason && isEndReason(reason)) return { source: 'endReason', key: reason };
  }
  return null;
}

/**
 * i18n key (namespace `backgammon`) for a descriptor. Kept next to the
 * resolver so a new reason cannot be added in one place and forgotten in the
 * other — `endReason.<key>` must exist in BOTH `en` and `it`.
 */
export function endDescriptorKey(d: NonNullable<QgammonEndDescriptor>): string {
  return `endReason.${d.key}`;
}

/**
 * Was this match played against the bot? Reads the TOP-LEVEL `whiteIsBot` /
 * `blackIsBot` — the player objects carry no `isBot`, which is what the old
 * type wrongly claimed. Absent flags (older API build) read as "not a bot",
 * i.e. no badge, which is the safe direction: it under-labels rather than
 * calling a human match practice.
 */
export function isBotMatch(m: QgammonMatchSummary): boolean {
  return m.whiteIsBot === true || m.blackIsBot === true;
}
